// D&D 5e movement rules — creature-space interaction.
//
// Rules implemented here:
//  • You may pass through the space of an ally, an Incapacitated creature,
//    a Tiny creature, or a creature two size categories different from you.
//  • Another creature's space is Difficult Terrain (2× cost) unless that
//    creature is Tiny or your ally.

const INCAPACITATING = ['incapacitated', 'paralyzed', 'petrified', 'stunned', 'unconscious'];

// Size rank by D&D category: tiny=0, small=1, medium=2, large=3, huge=4, gargantuan=5.
// When sizeCategory is absent, derive from grid footprint (size): 1→medium, 2→large, 3→huge, 4→gargantuan.
const CATEGORY_RANK = { tiny: 0, small: 1, medium: 2, large: 3, huge: 4, gargantuan: 5 };

export function sizeRank(token) {
  if (!token) return 2;
  const c = token.sizeCategory;
  if (c && CATEGORY_RANK[c] != null) return CATEGORY_RANK[c];
  const s = token.size || 1;
  if (s >= 4) return 5;
  if (s === 3) return 4;
  if (s === 2) return 3;
  return 2; // size 1 → Medium default
}

export function isIncapacitated(token) {
  const conds = token?.conditions;
  if (!conds || !conds.length) return false;
  for (const c of conds) if (INCAPACITATING.includes(c)) return true;
  return false;
}

// Alliance: player / friendlyNPC / summon form one team; monster the other.
// Objects have no team — callers should handle them separately (always hard block).
export function areAllies(a, b) {
  if (!a || !b) return false;
  if (a.id === b.id) return true;
  const friendly = (t) => t.type === 'player' || t.type === 'friendlyNPC' || t.type === 'summon';
  const aF = friendly(a);
  const bF = friendly(b);
  return aF === bF && a.type !== 'object' && b.type !== 'object';
}

// Returns 'block' | 'free' | 'difficult' — how `moving` may enter `other`'s space.
export function passRule(moving, other) {
  if (!other || other.id === moving.id) return 'free';
  if (other.defeated) return 'free';
  // Objects are physical obstacles — always a hard block. Movers that are
  // themselves objects (DM-dragged furniture) also hard-block on anything.
  if (moving.type === 'object' || other.type === 'object') return 'block';
  if (areAllies(moving, other)) return 'free';
  if (sizeRank(other) === 0) return 'free';                                 // Tiny
  if (Math.abs(sizeRank(moving) - sizeRank(other)) >= 2) return 'difficult';
  if (isIncapacitated(other)) return 'difficult';
  return 'block';
}

// Convenience: is the moving token prone? (all terrain becomes difficult for them)
export function isProne(token) {
  return !!(token?.conditions?.includes('prone'));
}
